import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, CircularProgress, LinearProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import {
  PayrollFlatFileSftpConfigQuery,
  useSetPayrollFlatFileSftpConfigMutation,
  usePayrollFlatFileSftpConfigQuery,
  useDeletePayrollFlatFileSftpConfigMutation,
  SftpConfigSetInput,
} from 'generated/graphql';
import OrganizationMenu from 'retired/OrganizationMenu';
import Box from 'retired/Box';
import { showErrorAction, showMessageAction } from 'containers/Notification/actions';
import { selectUser } from 'entities/user/selectors';
import { Features } from 'utils/features';

import SFTPConnectionForm from './SFTPConnectionForm';
import { STUB_PASSWORD } from './stubPassword';

const getDefaultValues = (
  payrollFlatFileSftpConfig?: PayrollFlatFileSftpConfigQuery['payrollFlatFileSftpConfig']
): Required<SftpConfigSetInput> => {
  if (!payrollFlatFileSftpConfig) {
    return {
      host: '',
      port: 22,
      username: '',
      password: '',
    };
  }

  const { host, port, username, isSecretSet } = payrollFlatFileSftpConfig;

  return {
    host,
    port: port || 22,
    username,
    // @NOTE-HF: We don't want to reveal password on the client side
    // To simplify form handling, we need a default password that is human readable
    // and is unlikely enough to conflict with the user input
    // This default password is then compared to the user input to decide if we should send the password field to the backend
    // We decided to go with "stub-password"
    password: isSecretSet ? STUB_PASSWORD : '',
  };
};

const SFTPConnectionFormPage = (): JSX.Element => {
  const user = useSelector(selectUser);
  const history = useHistory();

  const isPayrollFlatFileEnabled = user?.hasFeature(Features.PAYROLL_FLAT_FILE);

  if (!user?.isAdmin || !isPayrollFlatFileEnabled) {
    history.push('/');
  }

  const {
    data: payrollFlatFileSftpConfigQueryData,
    loading: payrollFlatFileSftpConfigQueryLoading,
    refetch: refetchPayrollFlatFileSftpConfig,
  } = usePayrollFlatFileSftpConfigQuery();
  const dispatch = useDispatch();

  const payrollFlatFileSftpConfig = payrollFlatFileSftpConfigQueryData?.payrollFlatFileSftpConfig;

  const formMethods = useForm<Required<SftpConfigSetInput>>({
    mode: 'all',
    defaultValues: getDefaultValues(payrollFlatFileSftpConfig),
  });

  const [setSftpConfigMutation, { loading: setSftpConfigMutationLoading }] =
    useSetPayrollFlatFileSftpConfigMutation({
      onCompleted: () => {
        void refetchPayrollFlatFileSftpConfig();
        dispatch(showMessageAction('Changes Saved!'));
      },
      onError: () => {
        dispatch(showErrorAction('An error occurred during saving, please try again.'));
      },
    });

  const [deleteSftpConfigMutation, { loading: deleteSftpConfigMutationLoading }] =
    useDeletePayrollFlatFileSftpConfigMutation({
      onCompleted: () => {
        formMethods.reset(getDefaultValues());
        void refetchPayrollFlatFileSftpConfig();
        dispatch(showMessageAction('Successfully Removed!'));
      },
      onError: () => {
        dispatch(showErrorAction('An error occurred, please try again.'));
      },
    });

  useEffect(() => {
    formMethods.reset(getDefaultValues(payrollFlatFileSftpConfig));
  }, [payrollFlatFileSftpConfig, formMethods]);

  const hasFormValuesChanged = formMethods.formState.isDirty;

  const saveButton = (
    <Button
      onClick={formMethods.handleSubmit((formValues: Required<SftpConfigSetInput>) => {
        const hasPasswordChanged = formValues.password !== STUB_PASSWORD;
        return setSftpConfigMutation({
          variables: {
            data: {
              host: formValues.host,
              port: Number(formValues.port),
              username: formValues.username,
              ...(hasPasswordChanged && formValues.password
                ? { password: formValues.password }
                : {}),
            },
          },
        });
      })}
      disabled={
        !hasFormValuesChanged || setSftpConfigMutationLoading || deleteSftpConfigMutationLoading
      }
      startIcon={setSftpConfigMutationLoading && <CircularProgress size="1rem" />}
      variant="contained"
    >
      Save
    </Button>
  );

  const deleteButton = (
    <Button
      onClick={() => {
        void deleteSftpConfigMutation();
      }}
      color="primary"
      variant="outlined"
      size="large"
      disabled={
        !payrollFlatFileSftpConfig ||
        setSftpConfigMutationLoading ||
        deleteSftpConfigMutationLoading
      }
      startIcon={deleteSftpConfigMutationLoading && <CircularProgress size="1rem" />}
    >
      Revoke access&nbsp;
      <CloseIcon />
    </Button>
  );

  return (
    <>
      <div style={{ marginBottom: 60 }}>
        <OrganizationMenu />
      </div>
      {payrollFlatFileSftpConfigQueryLoading ? (
        <LinearProgress />
      ) : (
        <Box style={{ paddingTop: 0, marginBottom: 30 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2>SFTP Connection Settings</h2>
            &nbsp;
            <Tooltip
              title="Your SFTP credentials will be securely stored in our system and used to connect to your
              company's SFTP server to access files securely."
            >
              <HelpOutlineOutlinedIcon />
            </Tooltip>
          </div>

          <FormProvider {...formMethods}>
            <SFTPConnectionForm
              formMethods={formMethods}
              isSecretSet={payrollFlatFileSftpConfig?.isSecretSet}
            />
          </FormProvider>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {deleteButton}
              {saveButton}
            </div>
          </div>
        </Box>
      )}
    </>
  );
};

export default SFTPConnectionFormPage;
